<script setup lang="ts">
import type { Payment } from "@/model/order/payment"
import type { RefundFailedTask } from "@/model/order/refund-failed-task"
import BookManualPaymentBox from "@/pages/Order/Payment/BookManualPaymentBox.vue"
import MollieRefundBox from "@/pages/Order/Payment/MollieRefundBox.vue"
import SuccessfulPaymentsList from "@/pages/Order/Payment/SuccessfulPaymentsList.vue"
import type { TasksProps } from "@/pages/Tasks/Tasks/tasks-props"
import { defineTranslations } from "@/utils/defineTranslations"
import { $currency, useHttpGet } from "@/vf"
import { computed, toRefs } from "vue"

const props = defineProps<TasksProps<RefundFailedTask>>()
const { task } = toRefs(props)

const orderProcessId = computed(() => task.value.orderProcessId)

const { data, refresh } = useHttpGet<{
    payments: Payment[]
}>(
    computed(() => `/order/confirmation/${orderProcessId.value}/data`),
    false,
)

const trans = defineTranslations({
    "failed_info": {
        de: "Rückerstattung {refundId} über {amount} zur Mollie-Zahlung {molliePaymentId} ist fehlgeschlagen.",
        en: "Refund {refundId} of {amount} for Mollie payment {molliePaymentId} failed.",
    },
    "payments.title": {
        de: "Zahlungen",
        en: "Payments",
    },
    "payments.empty": {
        de: "Keine Zahlungen gefunden.",
        en: "No payments found.",
    },
})
</script>

<template>
    <div class="p-5">
        <div class="alert alert-danger">
            <i class="fa fa-fw fa-triangle-exclamation mr-1"></i>
            {{
                trans("failed_info", {
                    refundId: task.failedRefundId ?? "",
                    amount: $currency(task.failedRefundAmount),
                    molliePaymentId: task.failedMolliePaymentId ?? "–",
                })
            }}
        </div>

        <div class="row">
            <div class="col-xl-7 col-lg-6">
                <b>{{ trans("payments.title") }}</b>
                <div class="text-muted py-3" v-if="data && data.payments.length === 0">
                    {{ trans("payments.empty") }}
                </div>
                <SuccessfulPaymentsList
                    v-if="data"
                    :payments="data.payments"
                    :view-mode="props.viewMode"
                    class="mt-2"
                />
            </div>

            <div class="col-xl-5 col-lg-6">
                <MollieRefundBox
                    :data-endpoint="`/order/confirmation/${orderProcessId}/refund-data`"
                    :refund-endpoint="`/order/confirmation/${orderProcessId}/refund`"
                    @refunded="refresh"
                />

                <BookManualPaymentBox
                    class="mt-5 bg-white"
                    :endpoint="`/order/confirmation/${orderProcessId}/book-manual-payment`"
                    @booked="refresh"
                />
            </div>
        </div>
    </div>
</template>
